import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Paper,
  Stack,
  Grid,
  Chip,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material'
import { Refresh, Send, Email, Build, QrCode2, Key } from '@mui/icons-material'
import { PageShell } from '@components/common/PageShell'
import { emailApi } from '@api/endpoints/email'
import { SmtpDiagnostics, EmailTestResponse } from '@api/types/email'
import {
  adminMaintenanceApi,
  type ResetDemoPasswordsResult,
  type RotateDerivedShareCodesResult,
} from '@api/endpoints/adminMaintenance'
import { useAuthStore } from '@store/authStore'
import { ConfirmDialog } from '@components/common/ConfirmDialog'

export default function AdminToolsPage() {
  const { user } = useAuthStore()

  // SMTP config diagnostics
  const [config, setConfig] = useState<SmtpDiagnostics | null>(null)
  const [configLoading, setConfigLoading] = useState(true)
  const [configError, setConfigError] = useState<string | null>(null)

  // Test send
  const [toEmail, setToEmail] = useState(user?.email ?? '')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<EmailTestResponse | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)

  // ---- Maintenance jobs ----
  // Each previews before it runs. Both change production data in bulk, and "how many rows would
  // this touch" is worth answering while the answer is still reversible.
  const [codesBusy, setCodesBusy] = useState(false)
  const [codesResult, setCodesResult] = useState<RotateDerivedShareCodesResult | null>(null)
  const [codesError, setCodesError] = useState<string | null>(null)
  const [excludeEmail, setExcludeEmail] = useState('admin@hadv.quest')

  const [demoBusy, setDemoBusy] = useState(false)
  const [demoResult, setDemoResult] = useState<ResetDemoPasswordsResult | null>(null)
  const [demoError, setDemoError] = useState<string | null>(null)
  const [demoPassword, setDemoPassword] = useState('')

  // Which irreversible run is waiting on its confirm dialog.
  const [confirmJob, setConfirmJob] = useState<'codes' | 'demo' | null>(null)

  // The preview counts are only true for the exclusion they were taken with. Editing the email
  // afterwards used to leave Rotate enabled, running against rows nobody had previewed, so a
  // change throws the preview away and Rotate waits for a fresh one.
  const handleExcludeEmailChange = (value: string) => {
    setExcludeEmail(value)
    setCodesResult((r) => (r?.dryRun ? null : r))
  }

  const runRotateCodes = async (dryRun: boolean) => {
    setCodesBusy(true)
    setCodesError(null)
    if (!dryRun) setCodesResult(null)
    try {
      setCodesResult(
        await adminMaintenanceApi.rotateDerivedShareCodes(dryRun, excludeEmail.trim() || undefined)
      )
    } catch (err) {
      setCodesError(err instanceof Error ? err.message : 'Failed to run')
    } finally {
      setCodesBusy(false)
    }
  }

  const runDemoPasswords = async (dryRun: boolean) => {
    setDemoBusy(true)
    setDemoError(null)
    if (!dryRun) setDemoResult(null)
    try {
      setDemoResult(
        await adminMaintenanceApi.resetDemoPasswords(dryRun, dryRun ? undefined : demoPassword)
      )
    } catch (err) {
      setDemoError(err instanceof Error ? err.message : 'Failed to run')
    } finally {
      setDemoBusy(false)
    }
  }

  const fetchConfig = useCallback(async () => {
    setConfigLoading(true)
    setConfigError(null)
    try {
      setConfig(await emailApi.getEmailTestConfig())
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to load SMTP configuration')
    } finally {
      setConfigLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  const handleSendTest = async () => {
    if (!toEmail.trim()) return
    setSending(true)
    setResult(null)
    setSendError(null)
    try {
      const res = await emailApi.sendEmailTest({ toEmail: toEmail.trim() })
      setResult(res)
      // A test send returns the effective config too — keep the panel in sync.
      if (res.diagnostics) setConfig(res.diagnostics)
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send test email')
    } finally {
      setSending(false)
    }
  }

  return (
    <PageShell title="Tools" subtitle="Administrative utilities and diagnostics.">
      <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Email color="primary" />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Email / SMTP
          </Typography>
          <Tooltip title="Reload SMTP configuration">
            <span>
              <IconButton onClick={fetchConfig} disabled={configLoading} size="small">
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {/* Effective SMTP configuration */}
        {configLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : configError ? (
          <Alert severity="error" onClose={() => setConfigError(null)}>
            {configError}
          </Alert>
        ) : config ? (
          <Grid container spacing={1.5}>
            <ConfigRow label="Host" value={`${config.smtpHost}:${config.smtpPort}`} />
            <ConfigRow label="From" value={`${config.fromName} <${config.fromEmail}>`} />
            <ConfigRow label="SSL/TLS" value={config.enableSsl ? 'Enabled' : 'Disabled'} />
            <ConfigRow label="Username" value={config.username || '(none)'} />
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" display="block">
                Credentials
              </Typography>
              <Chip
                size="small"
                label={config.hasCredentials ? 'Present' : 'Missing'}
                color={config.hasCredentials ? 'success' : 'warning'}
              />
            </Grid>
            <ConfigRow label="Config source" value={config.source} />
          </Grid>
        ) : null}

        <Divider sx={{ my: 3 }} />

        {/* Send a test email */}
        <Typography variant="subtitle1" gutterBottom>
          Send a test email
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sends a message immediately and reports the exact result. This is a live connectivity
          check — it does not queue a retry.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField
            label="Recipient email"
            type="email"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            fullWidth
            size="small"
            disabled={sending}
          />
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <Send />}
            onClick={handleSendTest}
            disabled={sending || !toEmail.trim()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {sending ? 'Sending…' : 'Send test'}
          </Button>
        </Stack>

        {sendError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSendError(null)}>
            {sendError}
          </Alert>
        )}

        {result && (
          <Alert
            severity={result.success ? 'success' : 'error'}
            sx={{ mt: 2 }}
            onClose={() => setResult(null)}
          >
            {result.success ? (
              <>Test email sent to {toEmail.trim()} at {new Date(result.sentAt).toLocaleString()}.</>
            ) : (
              <>
                <strong>SMTP send failed:</strong> {result.error || 'Unknown error'}
              </>
            )}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720, mt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Build color="primary" />
          <Typography variant="h6">Maintenance</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          One-off jobs that change data in bulk. Each previews first — read the numbers before
          running it for real.
        </Typography>

        {/* Share codes */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <QrCode2 fontSize="small" />
          <Typography variant="subtitle2">Retire leaked share codes</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Codes issued before the generator was fixed were built from the owner&apos;s email address,
          so the code disclosed it. Only codes that actually match the old pattern are replaced —
          anything already random is left alone. Retiring one stops it resolving for good.
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            label="Leave this account alone"
            value={excludeEmail}
            onChange={(e) => handleExcludeEmailChange(e.target.value)}
            disabled={codesBusy}
            // Full width on a phone so the two buttons wrap onto their own line rather than
            // being squeezed to nothing next to it.
            sx={{ minWidth: 260, width: { xs: '100%', sm: 'auto' } }}
          />
          <Button variant="outlined" onClick={() => runRotateCodes(true)} disabled={codesBusy}>
            {codesBusy ? 'Working...' : 'Preview'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => setConfirmJob('codes')}
            // Only offered once a preview has found something. Running blind is how a bulk job
            // gets pointed at the wrong rows.
            disabled={codesBusy || !codesResult?.dryRun || (codesResult?.derived ?? 0) === 0}
          >
            Rotate {codesResult?.dryRun ? codesResult.derived : ''}
          </Button>
        </Stack>
        {codesError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCodesError(null)}>
            {codesError}
          </Alert>
        )}
        {codesResult && (
          <Alert
            severity={codesResult.dryRun ? 'info' : 'success'}
            sx={{ mb: 3 }}
            onClose={() => setCodesResult(null)}
          >
            {codesResult.dryRun ? (
              <>
                {codesResult.derived} of {codesResult.usersScanned} accounts hold a code derived from
                their email. {codesResult.skipped} are already random or excluded.
                {codesResult.derived > 0 && ' Nothing has changed yet.'}
              </>
            ) : (
              <>
                Rotated {codesResult.rotated} share code{codesResult.rotated === 1 ? '' : 's'}. The
                previous codes no longer resolve.
              </>
            )}
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Demo passwords */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Key fontSize="small" />
          <Typography variant="subtitle2">Reset demo account passwords</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sets every <code>User*@hadv.quest</code> demo account to the same password. Matched on the
          seeder&apos;s own pattern, so accounts added by a later seed are covered. The admin and the
          backend service account do not match it.
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            label="New password"
            value={demoPassword}
            onChange={(e) => setDemoPassword(e.target.value)}
            sx={{ minWidth: 260, width: { xs: '100%', sm: 'auto' } }}
          />
          <Button variant="outlined" onClick={() => runDemoPasswords(true)} disabled={demoBusy}>
            {demoBusy ? 'Working...' : 'Preview'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => setConfirmJob('demo')}
            disabled={demoBusy || !demoResult?.dryRun || !demoPassword.trim()}
          >
            Reset {demoResult?.dryRun ? demoResult.matched : ''}
          </Button>
        </Stack>
        {demoError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDemoError(null)}>
            {demoError}
          </Alert>
        )}
        {demoResult && (
          <Alert
            severity={demoResult.dryRun ? 'info' : demoResult.failures.length ? 'warning' : 'success'}
            onClose={() => setDemoResult(null)}
          >
            {demoResult.dryRun ? (
              <>
                {demoResult.matched} demo account{demoResult.matched === 1 ? '' : 's'} matched:{' '}
                <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
                  {demoResult.accounts.join(', ') || '(none)'}
                </Box>
                . Nothing has changed yet.
              </>
            ) : (
              <>
                Reset {demoResult.reset} of {demoResult.matched}.
                {demoResult.failures.length > 0 && (
                  <> Failed: {demoResult.failures.join('; ')}</>
                )}
              </>
            )}
          </Alert>
        )}

      </Paper>

      <ConfirmDialog
        open={confirmJob === 'codes'}
        title={`Rotate ${codesResult?.derived ?? 0} share codes?`}
        message={
          <>
            The preview found <strong>{codesResult?.derived ?? 0}</strong> of{' '}
            {codesResult?.usersScanned ?? 0} accounts holding a code built from their email
            {excludeEmail.trim() ? <> (leaving {excludeEmail.trim()} alone)</> : null}. Each gets a
            new random code and the old one stops resolving for good, including any printed or
            shared links. This cannot be undone.
          </>
        }
        confirmLabel="Rotate codes"
        destructive
        onConfirm={() => {
          setConfirmJob(null)
          runRotateCodes(false)
        }}
        onClose={() => setConfirmJob(null)}
      />

      <ConfirmDialog
        open={confirmJob === 'demo'}
        title={`Reset ${demoResult?.matched ?? 0} demo passwords?`}
        message={
          <>
            Every one of the <strong>{demoResult?.matched ?? 0}</strong> matched demo accounts gets
            the new password, and their current passwords stop working. The old passwords cannot be
            recovered.
          </>
        }
        confirmLabel="Reset passwords"
        destructive
        onConfirm={() => {
          setConfirmJob(null)
          runDemoPasswords(false)
        }}
        onClose={() => setConfirmJob(null)}
      />
    </PageShell>
  )
}

function ConfigRow({ label, value }: { label: string; value: string }) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Grid>
  )
}
